import React, {useState, useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import {Range} from 'react-range';
import priceRangeStore from './price-range-store';
import {otherFieldPriceEnd, parsePriceRangeInput, PRICE_RANGE_STEP, priceRangeInputError, type PriceRangeField} from './price-range-input/parse-price-range-input';

const PRICE_RANGE_ERROR_ID = 'price-range-error';

function isSameRange(a: number[], b: number[]): boolean {
    return a[0] === b[0] && a[1] === b[1];
}

export const PriceRangeSlider: React.FC = observer(() => {
    const {range, min, max, setPriceRange, setMinValue, setMaxValue} = priceRangeStore;
    const [localMin, setLocalMin] = useState(String(range[0]));
    const [localMax, setLocalMax] = useState(String(range[1]));
    const [sliderValues, setSliderValues] = useState([range[0], range[1]]);
    const [rangeInvalid, setRangeInvalid] = useState(false);
    const announcementRef = useRef<HTMLDivElement>(null);
    const sliderValuesRef = useRef([range[0], range[1]]);
    const rangeError = rangeInvalid ? priceRangeInputError(min, max) : '';

    useEffect(() => {
        const next = [range[0], range[1]];
        sliderValuesRef.current = next;
        setSliderValues(next);
        setLocalMin(String(range[0]));
        setLocalMax(String(range[1]));
        setRangeInvalid(false);

        if (announcementRef.current) {
            announcementRef.current.textContent = range[0] !== min || range[1] !== max ? `Price range: $${range[0]} to $${range[1]}` : '';
        }
    }, [range, min, max]);

    const commitField = (field: PriceRangeField) => {
        const bounds = {
            min,
            max,
            step: PRICE_RANGE_STEP,
            appliedMin: range[0],
            appliedMax: range[1],
        };
        const otherEnd = otherFieldPriceEnd(field === 'min' ? localMax : localMin, field === 'min' ? 'max' : 'min', bounds);
        const result = parsePriceRangeInput(field === 'min' ? localMin : localMax, field, {
            ...bounds,
            appliedMin: field === 'max' ? otherEnd : bounds.appliedMin,
            appliedMax: field === 'min' ? otherEnd : bounds.appliedMax,
        });

        if (result.status === 'invalid') {
            setRangeInvalid(true);
            return;
        }

        setRangeInvalid(false);

        if (result.status === 'reset') {
            if (field === 'min') {
                setLocalMin(String(min));
                setMinValue(0);
            } else {
                setLocalMax(String(max));
                setMaxValue(0);
            }
            return;
        }

        if (field === 'min') {
            setLocalMin(String(result.value));
            setMinValue(result.value);
        } else {
            setLocalMax(String(result.value));
            setMaxValue(result.value);
        }
    };

    const onFieldKeyDown = (field: PriceRangeField) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitField(field);
        }
    };

    const onSliderChange = (values: number[]) => {
        const next = [values[0], values[1]];
        sliderValuesRef.current = next;
        setSliderValues(next);
        setRangeInvalid(false);
        setLocalMin(String(next[0]));
        setLocalMax(String(next[1]));
    };

    const onSliderFinalChange = () => {
        const next = sliderValuesRef.current;
        if (isSameRange(next, range)) {
            return;
        }

        setPriceRange(next);
    };

    return (
        <fieldset>
            <div
                ref={announcementRef}
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            />
            <legend className="d-inline-block">Price Range Filter</legend>
            <div className="price-range-fields">
                <div className="price-range-inputs">
                    <div className="price-range-field">
                        <label
                            htmlFor="min-price"
                            className="sr-only"
                        >
                            Minimum price
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            enterKeyHint="done"
                            autoComplete="off"
                            spellCheck={false}
                            id="min-price"
                            value={localMin}
                            onChange={e => {
                                setLocalMin(e.target.value);
                                setRangeInvalid(false);
                            }}
                            onBlur={() => commitField('min')}
                            onKeyDown={onFieldKeyDown('min')}
                            aria-invalid={rangeInvalid ? true : undefined}
                            aria-describedby={rangeInvalid ? PRICE_RANGE_ERROR_ID : undefined}
                        />
                    </div>
                    <span
                        className="price-range-separator"
                        aria-hidden="true"
                    >
                        {' '}
                        &minus;{' '}
                    </span>
                    <div className="price-range-field">
                        <label
                            htmlFor="max-price"
                            className="sr-only"
                        >
                            Maximum price
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            enterKeyHint="done"
                            autoComplete="off"
                            spellCheck={false}
                            id="max-price"
                            value={localMax}
                            onChange={e => {
                                setLocalMax(e.target.value);
                                setRangeInvalid(false);
                            }}
                            onBlur={() => commitField('max')}
                            onKeyDown={onFieldKeyDown('max')}
                            aria-invalid={rangeInvalid ? true : undefined}
                            aria-describedby={rangeInvalid ? PRICE_RANGE_ERROR_ID : undefined}
                        />
                    </div>
                </div>
                {rangeError ? (
                    <div
                        id={PRICE_RANGE_ERROR_ID}
                        className="price-range-field-error text-danger"
                    >
                        {rangeError}
                    </div>
                ) : null}
            </div>

            <Range
                step={PRICE_RANGE_STEP}
                min={min}
                max={max}
                values={sliderValues}
                onChange={onSliderChange}
                onFinalChange={onSliderFinalChange}
                renderTrack={({props, children}) => (
                    <div
                        {...props}
                        style={{...props.style}}
                        className="slider"
                    >
                        {children}
                    </div>
                )}
                renderThumb={({props}) => (
                    <div
                        {...props}
                        key={props.key}
                        style={{...props.style}}
                        className="thumb"
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                )}
            />
        </fieldset>
    );
});
